"use client";

import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { cn } from "@/lib/utils";
import ReservationForm from "@/components/menu/reservation-form/ReservationForm";
import { fetchCategories, type Category } from "@/lib/service/category.service";

type TabId = number | string | "all" | null;
type PriceSort = "none" | "asc" | "desc";

type FilterItem = { name: string; description?: string; price?: number };

type MenuItem = {
  id: number | string;
  name: string;
  description?: string | null;
  price: number | string;
  imageUrl?: string | null;
  category?: number | null;
  available?: boolean;
  filters?: FilterItem[] | null;
};

type Pagination = {
  page: number;
  limit: number;
  totalPages?: number;
  totalItems?: number;
  hasMore?: boolean;
};

/** ---------- Utils ---------- **/
const toNumber = (v: any): number | undefined => {
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const normFilters = (val?: any): FilterItem[] => {
  if (!val) return [];
  if (!Array.isArray(val)) return [];
  return val
    .map((x) => {
      if (typeof x === "string") return { name: x.trim() };
      if (x && typeof x === "object") {
        const name = String(x.name ?? "").trim();
        if (!name) return null;
        const description =
          x.description !== undefined && x.description !== null
            ? String(x.description)
            : undefined;
        const price =
          x.price !== undefined && x.price !== null
            ? toNumber(x.price)
            : undefined;
        return { name, description, price };
      }
      return null;
    })
    .filter(Boolean) as FilterItem[];
};

const formatMoney = (n?: number) =>
  typeof n === "number" && Number.isFinite(n)
    ? `৳ ${n.toFixed(2).replace(/\.00$/, "")}`
    : "৳ 0";

/** Price display: if filters exist, show "base - maxFilterPrice" else just base */
const getDisplayPrice = (item: MenuItem): string => {
  const base = toNumber(item.price) ?? 0;
  const filters = normFilters(item.filters);
  if (filters.length === 0) return formatMoney(base);

  const withPrices = filters
    .map((f) => toNumber(f.price))
    .filter((p): p is number => typeof p === "number");

  if (withPrices.length === 0) return formatMoney(base);

  const maxFilter = Math.max(...withPrices);
  return `${formatMoney(base)} - ${formatMoney(maxFilter)}`;
};

/** Get all effective numeric prices for an item (base + filter prices) */
const getAllPrices = (item: MenuItem): number[] => {
  const base = toNumber(item.price) ?? 0;
  const filters = normFilters(item.filters);
  const filterPrices = filters
    .map((f) => toNumber(f.price))
    .filter((p): p is number => typeof p === "number");
  return [base, ...filterPrices];
};

/** Get the minimum price for sorting/filtering */
const getMinPrice = (item: MenuItem): number => {
  const prices = getAllPrices(item);
  return Math.min(...prices);
};

/** Get the maximum price for sorting/filtering */
const getMaxPrice = (item: MenuItem): number => {
  const prices = getAllPrices(item);
  return Math.max(...prices);
};

/** Check if item overlaps with the given price range */
const itemInPriceRange = (
  item: MenuItem,
  minVal: number | undefined,
  maxVal: number | undefined,
): boolean => {
  if (minVal === undefined && maxVal === undefined) return true;
  const itemMin = getMinPrice(item);
  const itemMax = getMaxPrice(item);
  if (minVal !== undefined && itemMax < minVal) return false;
  if (maxVal !== undefined && itemMin > maxVal) return false;
  return true;
};

/** ✅ Safe data parser - handles multiple response formats */
const parseMenuData = (json: any): MenuItem[] => {
  // Try to find array in various possible keys
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json?.items)) return json.items;
  if (Array.isArray(json?.menu)) return json.menu; // ← Your API uses "menu"
  if (Array.isArray(json?.result)) return json.result;

  // Last resort: find first array in object
  const firstArr = Object.values(json || {}).find((v) => Array.isArray(v));
  if (firstArr && Array.isArray(firstArr)) return firstArr;

  return [];
};

/** ✅ Safe pagination parser */
const parsePagination = (json: any, defaultLimit: number = 6): Pagination => {
  const pag = json?.pagination || {};
  const limit = Number(pag.limit) || defaultLimit;
  const page = Number(pag.page) || 1;
  const totalItems = Number(pag.totalItems) || 0;
  const totalPages = pag.totalPages || Math.ceil(totalItems / limit) || 1;

  return {
    page,
    limit,
    totalPages,
    totalItems,
    hasMore: page < totalPages,
  };
};

export default function MenuPage() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [items, setItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<number | string>("all");
  const [query, setQuery] = useState("");
  const [priceSort, setPriceSort] = useState<PriceSort>("none");

  const [minPriceInput, setMinPriceInput] = useState<string>("");
  const [maxPriceInput, setMaxPriceInput] = useState<string>("");

  const [page, setPage] = useState<number>(1);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 6,
    totalPages: 1,
    totalItems: 0,
  });

  const minPriceFilter = useMemo(() => {
    const n = Number(minPriceInput);
    return minPriceInput.trim() !== "" && Number.isFinite(n) && n >= 0
      ? n
      : undefined;
  }, [minPriceInput]);

  const maxPriceFilter = useMemo(() => {
    const n = Number(maxPriceInput);
    return maxPriceInput.trim() !== "" && Number.isFinite(n) && n >= 0
      ? n
      : undefined;
  }, [maxPriceInput]);

  // Load categories
  useEffect(() => {
    (async () => {
      try {
        const cats =
          (await fetchCategories().catch(() => [] as Category[])) || [];
        setCategories(cats);
      } catch (e: any) {
        setError(e?.message || "Failed to load categories");
        console.error(e);
      }
    })();
  }, []);

  // ✅ Main fetch effect - works for BOTH All and Category tabs
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);

      try {
        let url = "";

        if (activeTab === "all") {
          // All items - fetch from main menu endpoint
          url = `/api/menus/getAll?page=${page}&limit=400`;
        } else {
          // Category items
          url = `/api/menu/category/${activeTab}?page=${page}&limit=6`;
        }

        console.log("Fetching:", url);

        const res = await fetch(url, { cache: "no-store" });
        if (!res.ok)
          throw new Error(`Failed to load menu items: ${res.status}`);

        const json = await res.json();

        // ✅ Parse data using our safe function
        const rawData = parseMenuData(json);
        const data = rawData.map((it: any) => ({
          ...it,
          filters: normFilters(it.filters),
        }));

        // ✅ Parse pagination
        const pag = parsePagination(json, 6);

        setItems(data);
        setPagination(pag);
      } catch (e: any) {
        console.error("Fetch error:", e);
        setError(e?.message || "Failed to load menu items");
        setItems([]);
        setPagination({ page: 1, limit: 6, totalPages: 1, totalItems: 0 });
      } finally {
        setLoading(false);
      }
    })();
  }, [activeTab, page]);

  // Reset to page 1 when tab changes
  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  const currentPage = pagination.page || page;
  const totalPages = pagination.totalPages || 1;
  const nextEnabled = currentPage < totalPages;

  // ✅ Search + Price filter + Sort
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = items
      .filter((it) => it.available !== false)
      .filter((it) =>
        q
          ? it.name.toLowerCase().includes(q) ||
            (it.description || "").toLowerCase().includes(q)
          : true,
      )
      .filter((it) => itemInPriceRange(it, minPriceFilter, maxPriceFilter));

    if (priceSort === "asc") {
      list = [...list].sort((a, b) => getMinPrice(a) - getMinPrice(b));
    } else if (priceSort === "desc") {
      list = [...list].sort((a, b) => getMaxPrice(b) - getMaxPrice(a));
    }

    return list;
  }, [items, query, priceSort, minPriceFilter, maxPriceFilter]);

  const offsetClasses: string[] = [
    "",
    "xl:-translate-y-[90px] 2xl:-translate-y-[190px]",
    "xl:-translate-y-[150px] 2xl:-translate-y-[180px]",
    "xl:-translate-y-[200px] 2xl:-translate-y-[350px]",
    "xl:-translate-y-[260px] 2xl:-translate-y-[380px]",
    "xl:-translate-y-[320px] 2xl:-translate-y-[480px]",
  ];

  const goToPage = (p: number) => {
    if (p < 1 || p > Math.max(1, totalPages)) return;
    setPage(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const makePageList = () => {
    const pages: number[] = [];
    const windowSize = 1;
    const add = (n: number) => {
      if (n >= 1 && n <= totalPages && !pages.includes(n)) pages.push(n);
    };
    add(1);
    add(totalPages);
    add(currentPage);
    for (let i = 1; i <= windowSize; i++) {
      add(currentPage - i);
      add(currentPage + i);
    }
    pages.sort((a, b) => a - b);
    const withGaps: (number | "gap")[] = [];
    for (let i = 0; i < pages.length; i++) {
      withGaps.push(pages[i]);
      if (i < pages.length - 1 && pages[i + 1] - pages[i] > 1) {
        withGaps.push("gap");
      }
    }
    return withGaps;
  };

  const isAllTab = activeTab === "all";
  const hasPriceFilter =
    minPriceFilter !== undefined || maxPriceFilter !== undefined;

  const clearPriceFilter = () => {
    setMinPriceInput("");
    setMaxPriceInput("");
  };

  return (
    <main>
      {/* Hero Section */}
      <section className="px-[30px]">
        <div className="h-[328.37px] sm:h-[370.39px] max-w-[1568px] w-full mx-auto bg-[url('/imgs/menu-hero-bg.png')] bg-cover bg-center bg-no-repeat pb-8 text-center rounded-b-[50px] overflow-hidden center items-end">
          <div>
            <h1 className="text-[33px] sm:text-4xl lg:text-6xl xl:text-[75px] leading-[86%] font-young-serif mb-2">
              Discover the Flavors
            </h1>
            <p className="text-sm sm:text-base lg:text-lg xl:text-[22px] px-2">
              Freshly prepared, served with care.
            </p>
            <div className="h-[43.67px] max-w-[248.02px] w-full mx-auto px-6 mt-[30px] flex items-center justify-between rounded-[7.15px] border border-old-lace/60 bg-black/[31%] backdrop-blur-[23.74px]">
              <input
                type="text"
                name="search"
                id="search"
                placeholder="Search any dish"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(1);
                }}
                className="grow h-full outline-0 border-0 text-sm text-white/55 font-semibold placeholder:text-white/55 placeholder:font-semibold bg-transparent"
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                className="text-white/70"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Menu Section */}
      <section className="relative xl:pb-0 pb-10 sm:pb-20">
        {/* Background shapes (unchanged) */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={116}
          height={310}
          viewBox="0 0 116 310"
          fill="none"
          className="xl:block hidden absolute top-20 left-0"
        >
          <circle
            cx="-38.6255"
            cy="154.962"
            r="154.356"
            stroke="#F0DC9C"
            strokeOpacity="0.5"
            strokeWidth="0.348901"
          />
        </svg>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={260}
          height={310}
          viewBox="0 0 260 310"
          fill="none"
          className="xl:block hidden absolute top-[350px] right-0"
        >
          <circle
            cx="155.507"
            cy="154.905"
            r="154.356"
            stroke="#F0DC9C"
            strokeOpacity="0.5"
            strokeWidth="0.348901"
          />
        </svg>
        <div className="xl:block hidden absolute bottom-1/2 left-0">
          <Image
            src="/imgs/shape-flower.png"
            alt="shape"
            width={200}
            height={318}
            className="z-50"
          />
        </div>
        <div className="xl:block hidden absolute bottom-0 right-0">
          <Image
            src="/imgs/shape-flower-2.png"
            alt="shape"
            width={200}
            height={318}
            className="z-50"
          />
        </div>

        {activeTab === "all" ? (
          <div className="max-w-[calc(1568px+60px)] px-7.5 w-full mx-auto ">
            {/* Tabs + Filters */}
            <div className="center mt-10 mb-10 lg:mb-[150px] relative isolate flex-col gap-5">
              {error && (
                <div className="mb-4 text-sm text-red-200 bg-red-900/30 border border-red-700/40 px-3 py-2 rounded-lg">
                  {error}
                </div>
              )}

              {/* Category Tabs */}
              <ul className="flex items-center justify-center gap-1.5 lg:gap-5.25 flex-wrap">
                <li>
                  <button
                    className={cn(
                      "tab_btn transform transition duration-300 ease-in-out",
                      activeTab === "all"
                        ? "tab_btn_active"
                        : "tab_btn_inactive",
                    )}
                    onClick={() => {
                      if (activeTab !== "all") {
                        setActiveTab("all");
                        setPage(1);
                      }
                    }}
                  >
                    <span>All</span>
                  </button>
                </li>

                {categories.map((c) => (
                  <li key={`tab-${c.id}`}>
                    <button
                      className={cn(
                        "tab_btn transform transition duration-300 ease-in-out",
                        Number(activeTab) === c.id
                          ? "tab_btn_active"
                          : "tab_btn_inactive",
                      )}
                      onClick={() => {
                        if (Number(activeTab) !== c.id) {
                          setActiveTab(c.id);
                          setPage(1);
                        }
                      }}
                    >
                      <span>{c.name}</span>
                    </button>
                  </li>
                ))}
              </ul>

              {/* Price Sort + Price Range Filter Row */}
              <div className="flex flex-col sm:flex-row items-center gap-3 flex-wrap justify-center">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white/60 font-medium">
                    Sort:
                  </span>
                  <div className="flex items-center gap-1.5 bg-black/30 border border-old-lace/20 rounded-lg p-1 backdrop-blur-sm">
                    <button
                      onClick={() => setPriceSort("none")}
                      className={cn(
                        "px-3 py-1 rounded-md text-xs font-semibold transition-all duration-200",
                        priceSort === "none"
                          ? "bg-old-gold text-black shadow-[0px_0px_10px_rgba(212,175,55,0.4)]"
                          : "text-white/60 hover:text-white/90 hover:bg-white/5",
                      )}
                    >
                      Default
                    </button>
                    <button
                      onClick={() => setPriceSort("asc")}
                      className={cn(
                        "px-3 py-1 rounded-md text-xs font-semibold transition-all duration-200 flex items-center gap-1",
                        priceSort === "asc"
                          ? "bg-old-gold text-black shadow-[0px_0px_10px_rgba(212,175,55,0.4)]"
                          : "text-white/60 hover:text-white/90 hover:bg-white/5",
                      )}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="12" y1="19" x2="12" y2="5" />
                        <polyline points="5 12 12 5 19 12" />
                      </svg>
                      Low to High
                    </button>
                    <button
                      onClick={() => setPriceSort("desc")}
                      className={cn(
                        "px-3 py-1 rounded-md text-xs font-semibold transition-all duration-200 flex items-center gap-1",
                        priceSort === "desc"
                          ? "bg-old-gold text-black shadow-[0px_0px_10px_rgba(212,175,55,0.4)]"
                          : "text-white/60 hover:text-white/90 hover:bg-white/5",
                      )}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <polyline points="19 12 12 19 5 12" />
                      </svg>
                      High to Low
                    </button>
                  </div>
                </div>

                {/* Price Range Inputs */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white/60 font-medium">
                    Price (৳):
                  </span>
                  <div className="flex items-center gap-1.5 bg-black/30 border border-old-lace/20 rounded-lg px-2 py-1 backdrop-blur-sm">
                    <input
                      type="number"
                      min="0"
                      placeholder="Min"
                      value={minPriceInput}
                      onChange={(e) => {
                        setMinPriceInput(e.target.value);
                        setPage(1);
                      }}
                      className="w-[68px] bg-transparent text-xs font-semibold text-white/80 placeholder:text-white/35 outline-none border border-white/15 rounded-md px-2 py-1 focus:border-old-gold/60 transition-colors [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                    <span className="text-white/30 text-xs">—</span>
                    <input
                      type="number"
                      min="0"
                      placeholder="Max"
                      value={maxPriceInput}
                      onChange={(e) => {
                        setMaxPriceInput(e.target.value);
                        setPage(1);
                      }}
                      className="w-[68px] bg-transparent text-xs font-semibold text-white/80 placeholder:text-white/35 outline-none border border-white/15 rounded-md px-2 py-1 focus:border-old-gold/60 transition-colors [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                    {hasPriceFilter && (
                      <button
                        onClick={clearPriceFilter}
                        title="Clear price filter"
                        className="ml-1 text-white/40 hover:text-old-gold transition-colors"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    )}
                  </div>
                  {hasPriceFilter && (
                    <span className="text-[10px] font-semibold text-old-gold/80 bg-old-gold/10 border border-old-gold/25 rounded-full px-2 py-0.5">
                      {minPriceFilter !== undefined &&
                      maxPriceFilter !== undefined
                        ? `৳${minPriceFilter} – ৳${maxPriceFilter}`
                        : minPriceFilter !== undefined
                          ? `≥ ৳${minPriceFilter}`
                          : `≤ ৳${maxPriceFilter}`}
                    </span>
                  )}
                </div>
              </div>

              {/* Results count */}
              {!loading && (
                <p className="text-xs text-white/35 -mt-1">
                  {visible.length} item{visible.length !== 1 ? "s" : ""} found
                </p>
              )}
            </div>

            {/* Items Grid */}
            <div className="w-full relative isolate">
              {visible.length === 0 ? null : (
                <>
                  <div className="xl:block hidden w-full absolute top-[0px] left-[52%] -translate-x-1/2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width={"100%"}
                      height={1839}
                      viewBox="0 0 1173 1139"
                      fill="none"
                      className="z-[-50]"
                    >
                      <path
                        d="M108.165 1.09589C108.165 1.09589 5.47254 138.995 133.087 234.802C260.702 330.609 427.098 203.751 637.816 106.991C848.535 10.2309 1184.43 -29.0792 1171.75 143.834C1159.08 316.748 283.781 242.649 154.018 341.041C24.2546 439.434 123.609 584.441 201.967 612.253C280.325 640.065 375.573 599.675 507.786 518.202C639.998 436.729 934.734 362.056 1023.49 474.269C1078.25 587.03 877.884 723.579 698.302 742.706C518.721 761.833 384.917 691 311.6 675.749C238.284 660.497 157.922 630.33 81.3134 698.156C4.70451 765.982 -69.996 945.205 123.609 1018.51C317.215 1091.81 795 705.515 971.393 752.27C1147.79 799.025 1159.79 1133.65 1018.21 1137.98"
                        stroke="white"
                        strokeWidth="1.5"
                      />
                    </svg>
                  </div>
                  <div className="h-full xl:hidden block absolute top-[0px] left-[52%] -translate-x-1/2 w-[1px] bg-white"></div>
                </>
              )}

              {loading ? (
                <LoadingSkeleton offsetClasses={offsetClasses} />
              ) : visible.length === 0 ? (
                <div className="py-16 text-center text-white/70">
                  <p className="text-lg font-semibold">No items found.</p>
                  {(query || hasPriceFilter) && (
                    <p className="text-sm mt-1 text-white/45">
                      Try adjusting your search or price filter.
                    </p>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-10 sm:gap-0">
                  {visible.map((item, index) => (
                    <div
                      key={`item-${item.id}`}
                      className={cn(
                        "flex items-center",
                        index % 2 !== 0 ? "self-end" : "self-start",
                        !isAllTab ? offsetClasses[index] || "" : "",
                      )}
                    >
                      {/* Image */}
                      <div
                        className={cn(
                          "w-[160px] xl:w-[351.4px] h-[120px] xl:h-[259.49px] relative center shrink-0",
                          index % 2 !== 0 ? "order-2" : "order-1",
                        )}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={352}
                          height={261}
                          viewBox="0 0 352 261"
                          fill="none"
                          className={cn(
                            "absolute top-0 -z-10 size-full",
                            index % 2 !== 0 ? "right-0" : "left-0",
                          )}
                        >
                          <rect
                            x="0.51875"
                            y="0.922681"
                            width="351.046"
                            height="259.14"
                            rx="14.825"
                            stroke="#F0DC9C"
                            strokeWidth="0.35"
                          />
                        </svg>
                        <Link
                          href={`/food/${item.id}`}
                          className="w-[140.73px] xl:w-[307.85px] h-[105px] xl:h-[227.33px] block"
                        >
                          <Image
                            src={
                              item.imageUrl && item.imageUrl.length > 0
                                ? item.imageUrl
                                : "/imgs/menu-item-1.png"
                            }
                            alt={item.name}
                            width={308}
                            height={228}
                            className="relative z-50 size-full object-cover"
                          />
                        </Link>
                      </div>

                      {/* Card */}
                      <div
                        className={cn(
                          "max-w-[228.17px] xl:max-w-[377.84px] w-full h-[98.51px] xl:h-[163.13px] center flex-col items-start bg-gradient-to-b from-[#151A1F] to-[#0E1317] shadow-[0px_0px_46.52px_0px_rgba(212,175,55,0.20)] z-20 relative border",
                          index % 2 !== 0
                            ? "order-1 pr-4 sm:pr-[60px] pl-2 sm:pl-3 sm:translate-x-[60px]"
                            : "order-2 pl-4 sm:pl-[60px] pr-2 sm:pr-3 sm:-translate-x-[60px]",
                        )}
                      >
                        <h1 className="text-[15.45px] xl:text-[25.59px] leading-[86%] font-bold font-amiri text-spring-wood">
                          {item.name}
                        </h1>
                        <p className="text-[9.83px] xl:text-[16.28px] leading-[102%] text-old-lace my-[7.02px] xl:my-4 line-clamp-2">
                          {item.description || "—"}
                        </p>
                        <div className="flex items-center gap-3">
                          <span className="xl:text-base text-[9.83px] font-semibold text-old-gold">
                            {getDisplayPrice(item)}
                          </span>
                          <Link
                            href={`/food/${item.id}`}
                            className="flex items-center xl:text-base text-[9.83px] text-old-gold gap-1 xl:gap-2 font-semibold"
                          >
                            Order now
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width={44}
                              height={13}
                              viewBox="0 0 44 13"
                              fill="none"
                            >
                              <line
                                x1="0.0488281"
                                y1="6.00169"
                                x2="31.9179"
                                y2="6.00169"
                                stroke="#D4AF37"
                                strokeWidth="1.163"
                              />
                              <circle
                                cx="37.733"
                                cy="6.58321"
                                r="5.76199"
                                fill="#D4AF37"
                                stroke="#D4AF37"
                                strokeWidth="0.106053"
                              />
                            </svg>
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="max-w-[calc(1568px+60px)] px-7.5 w-full mx-auto 2xl:max-h-[1536px]">
            {/* Tabs + Filters */}
            <div className="center mt-10 mb-10 lg:mb-[150px] relative isolate flex-col gap-5">
              {error && (
                <div className="mb-4 text-sm text-red-200 bg-red-900/30 border border-red-700/40 px-3 py-2 rounded-lg">
                  {error}
                </div>
              )}

              {/* Category Tabs */}
              <ul className="flex items-center justify-center gap-1.5 lg:gap-5.25 flex-wrap">
                <li>
                  <button
                    className={cn(
                      "tab_btn transform transition duration-300 ease-in-out",
                      activeTab === "all"
                        ? "tab_btn_active"
                        : "tab_btn_inactive",
                    )}
                    onClick={() => {
                      if (activeTab !== "all") {
                        setActiveTab("all");
                        setPage(1);
                      }
                    }}
                  >
                    <span>All</span>
                  </button>
                </li>

                {categories.map((c) => (
                  <li key={`tab-${c.id}`}>
                    <button
                      className={cn(
                        "tab_btn transform transition duration-300 ease-in-out",
                        activeTab === c.id
                          ? "tab_btn_active"
                          : "tab_btn_inactive",
                      )}
                      onClick={() => {
                        if (activeTab !== c.id) {
                          setActiveTab(c.id);
                          setPage(1);
                        }
                      }}
                    >
                      <span>{c.name}</span>
                    </button>
                  </li>
                ))}
              </ul>

              {/* Price Sort + Price Range Filter Row */}
              <div className="flex flex-col sm:flex-row items-center gap-3 flex-wrap justify-center">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white/60 font-medium">
                    Sort:
                  </span>
                  <div className="flex items-center gap-1.5 bg-black/30 border border-old-lace/20 rounded-lg p-1 backdrop-blur-sm">
                    <button
                      onClick={() => setPriceSort("none")}
                      className={cn(
                        "px-3 py-1 rounded-md text-xs font-semibold transition-all duration-200",
                        priceSort === "none"
                          ? "bg-old-gold text-black shadow-[0px_0px_10px_rgba(212,175,55,0.4)]"
                          : "text-white/60 hover:text-white/90 hover:bg-white/5",
                      )}
                    >
                      Default
                    </button>
                    <button
                      onClick={() => setPriceSort("asc")}
                      className={cn(
                        "px-3 py-1 rounded-md text-xs font-semibold transition-all duration-200 flex items-center gap-1",
                        priceSort === "asc"
                          ? "bg-old-gold text-black shadow-[0px_0px_10px_rgba(212,175,55,0.4)]"
                          : "text-white/60 hover:text-white/90 hover:bg-white/5",
                      )}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="12" y1="19" x2="12" y2="5" />
                        <polyline points="5 12 12 5 19 12" />
                      </svg>
                      Low to High
                    </button>
                    <button
                      onClick={() => setPriceSort("desc")}
                      className={cn(
                        "px-3 py-1 rounded-md text-xs font-semibold transition-all duration-200 flex items-center gap-1",
                        priceSort === "desc"
                          ? "bg-old-gold text-black shadow-[0px_0px_10px_rgba(212,175,55,0.4)]"
                          : "text-white/60 hover:text-white/90 hover:bg-white/5",
                      )}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <polyline points="19 12 12 19 5 12" />
                      </svg>
                      High to Low
                    </button>
                  </div>
                </div>

                {/* Price Range Inputs */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white/60 font-medium">
                    Price (৳):
                  </span>
                  <div className="flex items-center gap-1.5 bg-black/30 border border-old-lace/20 rounded-lg px-2 py-1 backdrop-blur-sm">
                    <input
                      type="number"
                      min="0"
                      placeholder="Min"
                      value={minPriceInput}
                      onChange={(e) => {
                        setMinPriceInput(e.target.value);
                        setPage(1);
                      }}
                      className="w-[68px] bg-transparent text-xs font-semibold text-white/80 placeholder:text-white/35 outline-none border border-white/15 rounded-md px-2 py-1 focus:border-old-gold/60 transition-colors [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                    <span className="text-white/30 text-xs">—</span>
                    <input
                      type="number"
                      min="0"
                      placeholder="Max"
                      value={maxPriceInput}
                      onChange={(e) => {
                        setMaxPriceInput(e.target.value);
                        setPage(1);
                      }}
                      className="w-[68px] bg-transparent text-xs font-semibold text-white/80 placeholder:text-white/35 outline-none border border-white/15 rounded-md px-2 py-1 focus:border-old-gold/60 transition-colors [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                    {hasPriceFilter && (
                      <button
                        onClick={clearPriceFilter}
                        title="Clear price filter"
                        className="ml-1 text-white/40 hover:text-old-gold transition-colors"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    )}
                  </div>
                  {hasPriceFilter && (
                    <span className="text-[10px] font-semibold text-old-gold/80 bg-old-gold/10 border border-old-gold/25 rounded-full px-2 py-0.5">
                      {minPriceFilter !== undefined &&
                      maxPriceFilter !== undefined
                        ? `৳${minPriceFilter} – ৳${maxPriceFilter}`
                        : minPriceFilter !== undefined
                          ? `≥ ৳${minPriceFilter}`
                          : `≤ ৳${maxPriceFilter}`}
                    </span>
                  )}
                </div>
              </div>

              {/* Results count */}
              {!loading && (
                <p className="text-xs text-white/35 -mt-1">
                  {visible.length} item{visible.length !== 1 ? "s" : ""} found
                </p>
              )}
            </div>

            {/* Items Grid */}
            <div className="w-full relative isolate">
              {visible.length === 0 ? null : (
                <>
                  <div className="xl:block hidden w-full absolute top-[0px] left-[52%] -translate-x-1/2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width={"100%"}
                      height={1139}
                      viewBox="0 0 1173 1139"
                      fill="none"
                      className="z-[-50]"
                    >
                      <path
                        d="M108.165 1.09589C108.165 1.09589 5.47254 138.995 133.087 234.802C260.702 330.609 427.098 203.751 637.816 106.991C848.535 10.2309 1184.43 -29.0792 1171.75 143.834C1159.08 316.748 283.781 242.649 154.018 341.041C24.2546 439.434 123.609 584.441 201.967 612.253C280.325 640.065 375.573 599.675 507.786 518.202C639.998 436.729 934.734 362.056 1023.49 474.269C1078.25 587.03 877.884 723.579 698.302 742.706C518.721 761.833 384.917 691 311.6 675.749C238.284 660.497 157.922 630.33 81.3134 698.156C4.70451 765.982 -69.996 945.205 123.609 1018.51C317.215 1091.81 795 705.515 971.393 752.27C1147.79 799.025 1159.79 1133.65 1018.21 1137.98"
                        stroke="white"
                        strokeWidth="1.5"
                      />
                    </svg>
                  </div>
                  <div className="h-full xl:hidden block absolute top-[0px] left-[52%] -translate-x-1/2 w-[1px] bg-white"></div>
                </>
              )}

              {loading ? (
                <LoadingSkeleton offsetClasses={offsetClasses} />
              ) : visible.length === 0 ? (
                <div className="py-16 text-center text-white/70">
                  <p className="text-lg font-semibold">No items found.</p>
                  {(query || hasPriceFilter) && (
                    <p className="text-sm mt-1 text-white/45">
                      Try adjusting your search or price filter.
                    </p>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-10 sm:gap-0">
                  {visible.map((item, index) => (
                    <div
                      key={`item-${item.id}`}
                      className={cn(
                        "flex items-center",
                        index % 2 !== 0 ? "self-end" : "self-start",
                        !isAllTab ? offsetClasses[index] || "" : "",
                      )}
                    >
                      {/* Image */}
                      <div
                        className={cn(
                          "w-[160px] xl:w-[351.4px] h-[120px] xl:h-[259.49px] relative center shrink-0",
                          index % 2 !== 0 ? "order-2" : "order-1",
                        )}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={352}
                          height={261}
                          viewBox="0 0 352 261"
                          fill="none"
                          className={cn(
                            "absolute top-0 -z-10 size-full",
                            index % 2 !== 0 ? "right-0" : "left-0",
                          )}
                        >
                          <rect
                            x="0.51875"
                            y="0.922681"
                            width="351.046"
                            height="259.14"
                            rx="14.825"
                            stroke="#F0DC9C"
                            strokeWidth="0.35"
                          />
                        </svg>
                        <Link
                          href={`/food/${item.id}`}
                          className="w-[140.73px] xl:w-[307.85px] h-[105px] xl:h-[227.33px] block"
                        >
                          <Image
                            src={
                              item.imageUrl && item.imageUrl.length > 0
                                ? item.imageUrl
                                : "/imgs/menu-item-1.png"
                            }
                            alt={item.name}
                            width={308}
                            height={228}
                            className="relative z-50 size-full object-cover"
                          />
                        </Link>
                      </div>

                      {/* Card */}
                      <div
                        className={cn(
                          "max-w-[228.17px] xl:max-w-[377.84px] w-full h-[98.51px] xl:h-[163.13px] center flex-col items-start bg-gradient-to-b from-[#151A1F] to-[#0E1317] shadow-[0px_0px_46.52px_0px_rgba(212,175,55,0.20)] z-20 relative border",
                          index % 2 !== 0
                            ? "order-1 pr-4 sm:pr-[60px] pl-2 sm:pl-3 sm:translate-x-[60px]"
                            : "order-2 pl-4 sm:pl-[60px] pr-2 sm:pr-3 sm:-translate-x-[60px]",
                        )}
                      >
                        <h1 className="text-[15.45px] xl:text-[25.59px] leading-[86%] font-bold font-amiri text-spring-wood">
                          {item.name}
                        </h1>
                        <p className="text-[9.83px] xl:text-[16.28px] leading-[102%] text-old-lace my-[7.02px] xl:my-4 line-clamp-2">
                          {item.description || "—"}
                        </p>
                        <div className="flex items-center gap-3">
                          <span className="xl:text-base text-[9.83px] font-semibold text-old-gold">
                            {getDisplayPrice(item)}
                          </span>
                          <Link
                            href={`/food/${item.id}`}
                            className="flex items-center xl:text-base text-[9.83px] text-old-gold gap-1 xl:gap-2 font-semibold"
                          >
                            Order now
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width={44}
                              height={13}
                              viewBox="0 0 44 13"
                              fill="none"
                            >
                              <line
                                x1="0.0488281"
                                y1="6.00169"
                                x2="31.9179"
                                y2="6.00169"
                                stroke="#D4AF37"
                                strokeWidth="1.163"
                              />
                              <circle
                                cx="37.733"
                                cy="6.58321"
                                r="5.76199"
                                fill="#D4AF37"
                                stroke="#D4AF37"
                                strokeWidth="0.106053"
                              />
                            </svg>
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Pagination - for ALL tabs */}
      {totalPages > 1 && (
        <div className="py-7.5 text-center relative">
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage <= 1}
              className={cn(
                "px-3.5 py-2 rounded-md border transition",
                currentPage <= 1
                  ? "cursor-not-allowed opacity-50 border-white/20 text-white/40"
                  : "border-old-gold/60 text-old-gold hover:border-old-gold hover:bg-white/5",
              )}
            >
              Prev
            </button>

            {makePageList().map((p, idx) =>
              p === "gap" ? (
                <span key={`gap-${idx}`} className="px-2 text-white/60">
                  …
                </span>
              ) : (
                <button
                  key={`p-${p}`}
                  disabled={p === currentPage}
                  onClick={() => goToPage(p as number)}
                  className={cn(
                    "min-w-9 h-9 px-3 rounded-md border transition",
                    p === currentPage
                      ? "border-old-gold bg-white/10 text-old-gold font-semibold shadow-[0px_0px_16px_rgba(212,175,55,0.25)]"
                      : "border-white/15 text-white/80 hover:border-old-gold/60 hover:text-old-gold",
                  )}
                >
                  {p}
                </button>
              ),
            )}

            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={!nextEnabled}
              className={cn(
                "px-3.5 py-2 rounded-md border transition",
                !nextEnabled
                  ? "cursor-not-allowed opacity-50 border-white/20 text-white/40"
                  : "border-old-gold/60 text-old-gold hover:border-old-gold hover:bg-white/5",
              )}
            >
              Next
            </button>
          </div>
        </div>
      )}

      <ReservationForm />
    </main>
  );
}

/** Skeleton Loader */
function LoadingSkeleton({ offsetClasses }: { offsetClasses: string[] }) {
  const rows = Array.from({ length: 6 }).map((_, i) => i);
  return (
    <div className="flex flex-col gap-10 sm:gap-0">
      {rows.map((index) => (
        <div
          key={`skeleton-${index}`}
          className={cn(
            "flex items-center",
            index % 2 !== 0 ? "self-end" : "self-start",
            offsetClasses[index] || "",
          )}
        >
          <div
            className={cn(
              "w-[160px] xl:w-[351.4px] h-[120px] xl:h-[259.49px] relative center shrink-0",
              index % 2 !== 0 ? "order-2" : "order-1",
            )}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={352}
              height={261}
              viewBox="0 0 352 261"
              fill="none"
              className={cn(
                "absolute top-0 -z-10 size-full",
                index % 2 !== 0 ? "right-0" : "left-0",
              )}
            >
              <rect
                x="0.51875"
                y="0.922681"
                width="351.046"
                height="259.14"
                rx="14.825"
                stroke="#F0DC9C"
                strokeWidth="0.35"
              />
            </svg>
            <div className="w-[140.73px] xl:w-[307.85px] h-[105px] xl:h-[227.33px] rounded-lg bg-white/10 animate-pulse" />
          </div>

          <div
            className={cn(
              "max-w-[228.17px] xl:max-w-[377.84px] w-full h-[98.51px] xl:h-[163.13px] center flex-col items-start bg-gradient-to-b from-[#151A1F] to-[#0E1317] shadow-[0px_0px_46.52px_0px_rgba(212,175,55,0.20)] z-20 relative border",
              index % 2 !== 0
                ? "order-1 pr-4 sm:pr-[60px] pl-2 sm:pl-3 sm:translate-x-[60px]"
                : "order-2 pl-4 sm:pl-[60px] pr-2 sm:pr-3 sm:-translate-x-[60px]",
            )}
          >
            <div className="w-2/3 h-4 rounded bg-white/10 animate-pulse" />
            <div className="w-full h-3 mt-2 rounded bg-white/10 animate-pulse" />
            <div className="w-5/6 h-3 mt-1.5 rounded bg-white/10 animate-pulse" />
            <div className="flex items-center gap-3 mt-3 w-full">
              <div className="h-4 w-16 rounded bg-white/10 animate-pulse" />
              <div className="h-4 w-24 rounded bg-white/10 animate-pulse" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
